/** @jsxImportSource @emotion/react */
import React, { useState, useMemo } from 'react'
import { css } from '@emotion/react'
import { Modal, Input, Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { MutationPreview, SlotSuggestion } from '../hooks/useGameMutation'
import { EvaluationResult } from '../hooks/useGameEdit'

interface PreflightValidationModalProps {
  visible: boolean
  preview: MutationPreview | null
  loading: boolean
  confirming: boolean
  error: string | null
  onConfirm: (overrideReason?: string) => Promise<boolean>
  onCancel: () => void
  onSelectAlternative: (slot: SlotSuggestion) => void
}

const PreflightValidationModal: React.FC<PreflightValidationModalProps> = ({
  visible,
  preview,
  loading,
  confirming,
  error,
  onConfirm,
  onCancel,
  onSelectAlternative,
}) => {
  const [overrideReason, setOverrideReason] = useState('')

  const hasHardViolations = (preview?.hardViolations?.length ?? 0) > 0
  const needsOverride = hasHardViolations
  const canConfirm = !needsOverride || overrideReason.trim().length >= 10

  const title = useMemo(() => {
    if (!preview) {
      return 'Review Changes'
    }
    const shortId = (id: string) => (id.length > 8 ? `...${id.slice(-6)}` : id)
    if (preview.type === 'swap') {
      return `Swap Games ${shortId(preview.gameId)} ↔ ${shortId(
        preview.secondGameId ?? ''
      )}`
    }
    return `Review Changes — Game ${shortId(preview.gameId)}`
  }, [preview])

  const handleConfirm = async () => {
    const result = needsOverride
      ? await onConfirm(overrideReason)
      : await onConfirm()
    // Only clear override reason on success — preserve it on failure so user doesn't re-type
    if (result !== false) {
      setOverrideReason('')
    }
  }

  const handleCancel = () => {
    setOverrideReason('')
    onCancel()
  }

  return (
    <Modal
      open={visible}
      title={null}
      onCancel={handleCancel}
      footer={null}
      width={560}
      destroyOnClose
      styles={{
        content: {
          background: colors.HEADER,
          border: `1px solid ${colors.SOFT_STEEL}`,
          borderRadius: 12,
          padding: 0,
        },
        mask: { background: 'rgba(0,0,0,0.6)' },
      }}
    >
      {loading ? (
        <div css={loadingContainerStyles}>
          <Spin size='large' />
          <span css={loadingTextStyles}>Evaluating changes...</span>
        </div>
      ) : preview ? (
        <div css={contentStyles}>
          {/* Header */}
          <div css={headerStyles}>
            <span css={titleStyles}>{title}</span>
          </div>

          {/* Score Delta */}
          <ScoreDeltaBar
            currentScore={preview.currentScore}
            newScore={preview.newScore}
            delta={preview.scoreDelta}
          />

          {/* Violations */}
          {preview.hardViolations.length > 0 && (
            <ViolationSection
              label='Hard Violations'
              violations={preview.hardViolations}
              color={colors.ERROR_300}
            />
          )}

          {preview.softViolations.length > 0 && (
            <ViolationSection
              label='Soft Violations'
              violations={preview.softViolations}
              color={colors.WARNING_300}
            />
          )}

          {/* No violations */}
          {preview.hardViolations.length === 0 &&
            preview.softViolations.length === 0 && (
              <div css={noViolationsStyles}>
                No constraint violations detected.
              </div>
            )}

          {/* Override Section */}
          {needsOverride && (
            <div css={overrideSectionStyles}>
              <span css={overrideLabelStyles}>
                Override Reason (required for hard violations)
              </span>
              <Input.TextArea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder='Explain why this override is acceptable (min 10 characters)...'
                rows={3}
                styles={{
                  textarea: {
                    background: colors.SECONDARY_300,
                    borderColor: colors.SOFT_STEEL,
                    color: colors.WHITE,
                    fontSize: 12,
                  },
                }}
              />
            </div>
          )}

          {/* Alternative Slots */}
          {preview.alternativeSlots.length > 0 &&
            (preview.hardViolations.length > 0 ||
              preview.softViolations.length > 0) && (
              <div css={alternativeSectionStyles}>
                <span css={sectionLabelStyles}>Alternative Slots</span>
                <div css={alternativeListStyles}>
                  {preview.alternativeSlots.map((slot, i) => (
                    <AlternativeSlotCard
                      key={i}
                      slot={slot}
                      onClick={() => onSelectAlternative(slot)}
                    />
                  ))}
                </div>
              </div>
            )}

          {/* Error */}
          {error && <div css={errorStyles}>{error}</div>}

          {/* Actions */}
          <div css={actionsStyles}>
            <button css={cancelButtonStyles} onClick={handleCancel}>
              Cancel
            </button>
            <button
              css={confirmButtonStyles(canConfirm)}
              onClick={handleConfirm}
              disabled={!canConfirm || confirming}
            >
              {confirming
                ? 'Saving...'
                : needsOverride
                ? 'Force Save'
                : 'Confirm'}
            </button>
          </div>
        </div>
      ) : error ? (
        <div css={contentStyles}>
          <div css={headerStyles}>
            <span css={titleStyles}>Swap Preview</span>
          </div>
          <div css={errorStyles}>{error}</div>
          <div css={actionsStyles}>
            <button css={cancelButtonStyles} onClick={handleCancel}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  )
}

// Sub-components

const ScoreDeltaBar: React.FC<{
  currentScore: number
  newScore: number
  delta: number
}> = ({ currentScore, newScore, delta }) => {
  const deltaColor =
    delta > 0
      ? colors.SUCCESS_300
      : delta < -2
      ? colors.ERROR_300
      : delta < 0
      ? colors.WARNING_300
      : colors.NEUTRAL_50

  return (
    <div css={scoreDeltaContainerStyles}>
      <span css={scoreValueStyles}>{currentScore.toFixed(1)}%</span>
      <span css={scoreArrowStyles}>→</span>
      <span
        css={css`
          ${scoreValueStyles};
          color: ${deltaColor};
        `}
      >
        {newScore.toFixed(1)}%
      </span>
      {delta !== 0 && (
        <span
          css={css`
            font-size: 12px;
            font-weight: 600;
            color: ${deltaColor};
            margin-left: 4px;
          `}
        >
          ({delta > 0 ? '+' : ''}
          {delta.toFixed(1)}%)
        </span>
      )}
    </div>
  )
}

type ViolationType = EvaluationResult['violations'][0]

const ViolationSection: React.FC<{
  label: string
  violations: ViolationType[]
  color: string
}> = ({ label, violations, color }) => (
  <div css={violationSectionStyles}>
    <div css={sectionHeaderStyles}>
      <span css={violationBadgeStyles(color)}>{label}</span>
      <span css={violationCountStyles}>{violations.length}</span>
    </div>
    <div css={violationListStyles}>
      {violations.map((v, i) => (
        <div key={i} css={violationRowStyles}>
          <div css={violationHeaderRowStyles}>
            {v.constraint_name && (
              <span css={violationConstraintNameStyles}>
                {v.constraint_name}
              </span>
            )}
          </div>
          <span css={violationMessageStyles}>{v.message}</span>
        </div>
      ))}
    </div>
  </div>
)

const AlternativeSlotCard: React.FC<{
  slot: SlotSuggestion
  onClick: () => void
}> = ({ slot, onClick }) => {
  // Parse date/time as UTC and display as-is (schedule times are stored in UTC)
  const d = new Date(slot.starts_at)
  const dateStr = d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
  const timeStr = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })

  return (
    <button css={alternativeCardStyles} onClick={onClick}>
      <div css={alternativeCardTopStyles}>
        <span css={alternativeDateStyles}>{dateStr}</span>
        <span css={alternativeTimeStyles}>{timeStr}</span>
      </div>
      <span css={alternativeVenueStyles}>
        {slot.venue_name} — {slot.facility_name}
      </span>
      <span css={alternativeScoreStyles}>
        Score: {slot.satisfaction_score.toFixed(1)}%
      </span>
    </button>
  )
}

// Styles

const loadingContainerStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 60px 0;
`

const loadingTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_50};
`

const contentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`

const headerStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const titleStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.WHITE};
`

const scoreDeltaContainerStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${colors.SECONDARY_300};
  border-radius: 8px;
`

const scoreValueStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${colors.WHITE};
`

const scoreArrowStyles = css`
  font-size: 16px;
  color: ${colors.NEUTRAL_50};
`

const violationSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const sectionHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const violationCountStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.NEUTRAL_50};
`

const violationBadgeStyles = (color: string) => css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${color};
  background: ${color}1A;
  padding: 1px 5px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const violationListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const violationRowStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  background: ${colors.SECONDARY_300};
  border-radius: 6px;
`

const violationHeaderRowStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
`

const violationConstraintNameStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const violationMessageStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${colors.NEUTRAL_70};
  line-height: 1.3;
`

const noViolationsStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.SUCCESS_300};
  padding: 12px 16px;
  background: ${colors.SUCCESS_300}1A;
  border-radius: 8px;
`

const overrideSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: ${colors.ERROR_300}0D;
  border: 1px solid ${colors.ERROR_300}33;
  border-radius: 8px;
`

const overrideLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.ERROR_300};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const sectionLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const alternativeSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const alternativeListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
`

const alternativeCardStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  color: inherit;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${colors.PRIMARY_300};
  }
`

const alternativeCardTopStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const alternativeDateStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.WHITE};
`

const alternativeTimeStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
`

const alternativeVenueStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
`

const alternativeScoreStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.SUCCESS_300};
`

const errorStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.ERROR_300};
  padding: 8px 12px;
  background: ${colors.ERROR_300}1A;
  border-radius: 6px;
`

const actionsStyles = css`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid ${colors.SOFT_STEEL};
`

const cancelButtonStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.NEUTRAL_50};
  background: transparent;
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`

const confirmButtonStyles = (enabled: boolean) => css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.WHITE};
  background: ${enabled ? colors.PRIMARY_300 : colors.SECONDARY_300};
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  cursor: ${enabled ? 'pointer' : 'not-allowed'};
  opacity: ${enabled ? 1 : 0.5};
  transition: background 0.15s;

  &:hover {
    background: ${enabled ? colors.PRIMARY_200 : colors.SECONDARY_300};
  }
`

export default PreflightValidationModal
